import { useCallback, useEffect, useMemo, useState } from "react";
import { LanguagesAbbr, Survey, SurveyQuestion, SurveyQuestionType } from "../../../../../../types/working-model";
import { TypographyItem } from "../../../../../general-ui/typography-item/typography-item";
import classNames from 'classnames';
import Textarea from "../../../../../general-ui/textarea/textarea";
import { TemplateClassNames } from "../../../../../../types/template-layouts";
import '../../../../../live-event/session/session-modules/survey/survey.scss';
import './surveys.scss';
import Icon, { COLORS, ICONS } from "../../../../../general-ui/icon";
import { useTranslation } from "react-i18next";

interface SurveyProps {
	content: any;
	survey: Survey;
	lang: LanguagesAbbr;
	template: TemplateClassNames;
}

const EditorSurvey: React.FC<SurveyProps> = ({ content, survey, lang, template }) => {
	const [viewAllQuestions, setViewAllQuestions] = useState(false);
	const [currentQuestion, setCurrentQuestion] = useState(0);
	const { t } = useTranslation('session');
	const maxQuestions = survey?.questions?.length;

	useEffect(() => {
		if (survey.questions.length <= 1) {
			setViewAllQuestions(true);
		}
	}, [survey]);

	const displayQuestion = useCallback((question: SurveyQuestion) => {
		if (!question) return null;
		if (question.question_type === SurveyQuestionType.text) {
			return (
				<div
					className="survey-results text-result question-container"
					key={question.survey_question}
				>
					<TypographyItem className={classNames('question-title evt-heading-4', template)}>
						{question?.title?.[lang] ?? question?.title?.base}
					</TypographyItem>
					<div className="survey-text-actions-container">
						<Textarea placeholder={t("Enter your repsonse here")} />
						<div className={classNames('submit-action')}><button className={classNames('primary next')} type="submit" >{content?.callToAction?.button_name?.[lang] || t("Submit")}</button></div>
					</div>
				</div>
			);
		}
		if (question.question_type === SurveyQuestionType.radio) {
			let remaining = 100;

			const percentages = question.options.map(() => {
				if (remaining > 1) {
					const amount = Math.floor(Math.random() * 60);
					remaining = remaining - amount;
					return amount;
				} else {
					return 0;
				}
			});

			if (remaining > 0) {
				percentages[percentages.length - 1] =
					remaining + percentages[percentages.length - 1];
			}

			return (
				<div
					key={question.survey_question}
					className="survey-results radio-results question-container"
				>
					<TypographyItem className={classNames('question-title evt-heading-4', template)}>
						{question.title[lang] ?? question.title.base}
					</TypographyItem>
					{question.options.map((option, index) => (
						<div
							key={option.survey_question_option}
							className="survey-option-wrapper"
						>
							<div className="survey-option-container">
								<div
									className="survey-option focus-visible"
								>
									<div className="survey-option-progress-bar-container">
										<div className="survey-option-progress-bar" style={survey.show_results ? { width: `${percentages[index]}%` } : { width: '0%' }} />
									</div>
									<div className="survey-option-content">
										<div className="survey-option-title">{option.label[lang] ?? option.label.base}</div>
										<div className="survey-option-check">
											<div>
												<Icon name={ICONS.CHECK_CIRCLE_FILLED} color={COLORS.WHITE} size={16} />
											</div>
											{survey.show_results ? (
												<div className="survey-option-percentage-text">{`${percentages[index]}%`}</div>
											) : (<></>)}
										</div>
									</div>
								</div>
							</div>
						</div>
					))}
					<div className={classNames('submit-action')}><button className={classNames('primary next')} type="submit" >{content?.callToAction?.button_name?.[lang] || t("Submit")}</button></div>
				</div>
			);
		}

		if (question.question_type === SurveyQuestionType.checkbox) {
			const amounts = question.options.map(() => {
				return Math.min(100, Math.floor(Math.random() * 100));
			});

			return (
				<div
					key={question.survey_question}
					className="survey-results radio-results question-container"
				>
					<TypographyItem className={classNames('question-title evt-heading-4', template)}>
						{question.title[lang] ?? question.title.base}
					</TypographyItem>
					{question.options.map((option, index) => (
						<div
							key={option.survey_question_option}
							className="survey-option-wrapper"
						>
							<div className="survey-option-container">
								<div
									className="survey-option focus-visible"
								>
									<div className="survey-option-progress-bar-container">
										<div className="survey-option-progress-bar" style={survey.show_results ? { width: `${amounts[index]}%` } : { width: '0%' }} />
									</div>
									<div className="survey-option-content">
										<div className="survey-option-title">{option.label[lang] ?? option.label.base}</div>
										<div className="survey-option-check">
											<div>
												<Icon name={ICONS.CHECK_CIRCLE_FILLED} color={COLORS.WHITE} size={16} />
											</div>
											{survey.show_results ? (
												<div className="survey-option-percentage-text">{`${amounts[index]}%`}</div>
											) : (<></>)}
										</div>
									</div>
								</div>
							</div>
						</div>

					))}
					<div className={classNames('submit-action')}><button className={classNames('primary next')} type="submit" >{content?.callToAction?.button_name?.[lang] || t("Submit")}</button></div>
				</div>
			);
		}
	}, [content?.callToAction?.button_name, lang, survey.show_results, t, template]);

	// rerender list if changes are made to current survey or language
	const renderList = useMemo(() => {
		return survey.questions.map(displayQuestion);
	}, [survey, displayQuestion]);

	// rerender current question if changes are made to current survey
	const renderCollapsed = useMemo(() => {
		return displayQuestion(survey.questions[currentQuestion]);
	}, [survey, currentQuestion, displayQuestion]);


	return (
		<div className="survey-container" key={survey.survey}>
			<TypographyItem className={classNames('survey-title')} tagName="p">
				{survey?.name?.[lang] || survey?.name?.base}
			</TypographyItem>
			<div className={'question-list'}>
				<div className={classNames('view-all')}>
					{survey.questions.length > 1 ? (
						<button className='no-style clear' onClick={() => setViewAllQuestions(!viewAllQuestions)}>{viewAllQuestions ? t("Collapse Questions") : t("View All Questions")}</button>
					) : (<></>)}
					{survey.allow_retake && (
						<button className="no-style clear retake-button">{t("Retake Survey")}</button>
					)}
				</div>
				{viewAllQuestions ? renderList : (
					<>
						{renderCollapsed}
						<div className='survey-pagination'>
							<button disabled={currentQuestion === 0} onClick={() => setCurrentQuestion(currentQuestion - 1)}>{t("Previous")}</button>
							<span>{currentQuestion + 1}/{maxQuestions}</span>
							<button disabled={currentQuestion + 1 === maxQuestions} onClick={() => setCurrentQuestion(currentQuestion + 1)}>{t("Next")}</button>
						</div>
					</>
				)}
			</div>
		</div>
	);
};

export default EditorSurvey;